import React, { FC, useCallback } from "react";
import { useGetProjectByIdQuery } from "../../api/projectsApi";
import Button, { ButtonModeE } from "../../components/Button/Button";
import { VersionT } from "../../models/models";
import usePopup from "../../utils/hooks/usePopup";

import MarkNotSuitableForBroadcastingBody from "./MarkNotSuitableForBroadcastingBody";
import { MARK_NOT_SUITABLE_FORM_ID } from "./markNotSuitableFormId";
import {
  MarkNotSuitableFormDirtyProvider,
  useMarkNotSuitableFormDirtyState,
} from "./markNotSuitableFormDirtyContext";
import styles from "./markNotSuitableForBroadcasting.module.scss";
import { MarkNotSuitableRefetchProvider } from "./markNotSuitableRefetchContext";
import Loader from "../../components/Loader/Loader";

const MarkNotSuitableForBroadcastingFooter: FC = () => {
  const { removePopup } = usePopup();
  const { isDirty } = useMarkNotSuitableFormDirtyState();

  return (
    <div className={styles.footerButtonsWrapper}>
      <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
        Cancel
      </Button>
      <Button
        form={MARK_NOT_SUITABLE_FORM_ID}
        useGlobalLoading
        disabled={!isDirty}
      >
        Save and close
      </Button>
    </div>
  );
};

type MarkNotSuitableForBroadcastingPopupContentPropTypes = {
  projectId: number;
  versionAdId: string;
  /** Parent refetch (e.g. ViewVideoProject) so row `item` updates; RTK key may differ from popup query. */
  refetchProject?: () => void | Promise<unknown>;
};

/**
 * Wraps body + footer so project refetch from useGetProjectByIdQuery lives with the footer module.
 */
export const MarkNotSuitableForBroadcastingPopupContent: FC<
  MarkNotSuitableForBroadcastingPopupContentPropTypes
> = ({ projectId, versionAdId, refetchProject: refetchProjectFromParent }) => {
  const { data: projectData, refetch: refetchProjectData } = useGetProjectByIdQuery(
    { id: projectId, detailed: true },
    { skip: !projectId }
  );

  const versionData = projectData?.Versions?.find((v: VersionT) => v.AdId === versionAdId);

  const handleSubmit = useCallback(async () => {
    await refetchProjectFromParent?.();
    if (projectId) {
      await refetchProjectData();
    }
  }, [projectId, refetchProjectData, refetchProjectFromParent]);

  return projectData ? (
    <MarkNotSuitableFormDirtyProvider>
      <MarkNotSuitableRefetchProvider
        value={projectId ? handleSubmit : null}
      >
        <MarkNotSuitableForBroadcastingBody versionData={versionData} />
        <MarkNotSuitableForBroadcastingFooter />
      </MarkNotSuitableRefetchProvider>
    </MarkNotSuitableFormDirtyProvider>
  ) : <Loader  />;
};

export default MarkNotSuitableForBroadcastingFooter;
